import React, { useState, useEffect } from 'react';
import { Video, Film, Loader2, ChevronDown, ChevronRight, WifiOff } from 'lucide-react';
import EventDetailsCell from './EventDetailsCell';

// ── Site name mapping ─────────────────────────────────────────────────────────
const siteCodeMapping = {
  "001":"Songloed","002":"Zandput","003":"Eerstegoud","004":"Knobel",
  "005":"Soekmekaar","006":"Singo","007":"Hlanganani","008":"Rebolwa",
  "009":"Venulu","010":"Thomo","011":"Malamulele","012":"Muledani",
  "013":"Shayandima","014":"Tshikweta","015":"Makonde","016":"Sanari",
  "017":"Pondrift","018":"Silica","019":"Polokwane","020":"Louis Trichardt"
};

const getSiteName = (accountNumber) => {
  if (!accountNumber || accountNumber.length < 7) return null;
  return siteCodeMapping[accountNumber.substring(4, 7)] || null;
};

const formatDate = (dateString) => {
  try { return new Date(dateString).toLocaleDateString(); } catch { return dateString; }
};

const formatTime = (dateString) => {
  try { return new Date(dateString).toLocaleTimeString(); } catch { return ''; }
};

// ── Badge helpers (mirrors EventDetailsCell) ──────────────────────────────────
const getSeverityBadgeStyle = (severity) => {
  switch (severity) {
    case 'HIGH':    return 'bg-red-100 text-red-800 border border-red-200';
    case 'MEDIUM':  return 'bg-orange-100 text-orange-800 border border-orange-200';
    case 'WARNING': return 'bg-yellow-100 text-yellow-800 border border-yellow-200';
    case 'INFO':    return 'bg-green-100 text-green-800 border border-green-200';
    default:        return 'bg-gray-100 text-gray-800 border border-gray-200';
  }
};

const getCategoryBadgeStyle = (category) => {
  switch (category) {
    case 'ALARM':         return 'bg-red-50 text-red-700 border border-red-100';
    case 'VIDEO_ALARM':   return 'bg-purple-50 text-purple-700 border border-purple-100';
    case 'DURESS':        return 'bg-red-50 text-red-700 border border-red-100';
    case 'POWER':         return 'bg-amber-50 text-amber-700 border border-amber-100';
    case 'COMMUNICATION': return 'bg-blue-50 text-blue-700 border border-blue-100';
    case 'SYSTEM':        return 'bg-indigo-50 text-indigo-700 border border-indigo-100';
    case 'MAINTENANCE':   return 'bg-cyan-50 text-cyan-700 border border-cyan-100';
    case 'DEVICE':        return 'bg-teal-50 text-teal-700 border border-teal-100';
    case 'ACCESS':        return 'bg-emerald-50 text-emerald-700 border border-emerald-100';
    default:              return 'bg-gray-50 text-gray-700 border border-gray-100';
  }
};

// ── Main component ─────────────────────────────────────────────────────────────
export const ClusteredEventsTable = ({
  events,
  filterStatus,
  onAcknowledge,
  isLoading,
  onViewVideo,
  onDownloadVideo,
  panelStatuses,
  isPanelOnline
}) => {
  const [clusteredEvents, setClusteredEvents] = useState([]);
  const [expandedClusters, setExpandedClusters] = useState(new Set());

  // ── Clustering logic ──────────────────────────────────────────────────────
  useEffect(() => {
    if (!events || events.length === 0) return;

    const clusters = [];
    const processedIds = new Set();
    const sortedEvents = [...events].sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

    // First pass: non-INFO as primary
    sortedEvents.forEach(event => {
      if (processedIds.has(event._id)) return;

      const matchesFilter =
        filterStatus === 'ALL' ||
        (filterStatus === 'NEEDS_REVIEW' && event.alarmStatus === 'PENDING') ||
        (filterStatus === 'CONFIRMED' && event.alarmStatus === 'CONFIRMED') ||
        (filterStatus === 'FALSE' && event.alarmStatus === 'FALSE') ||
        (filterStatus === 'DEFAULT' && event.alarmStatus === 'DEFAULT');

      if (!matchesFilter) return;

      const eventSeverity = event.severity || 'UNKNOWN';
      if (eventSeverity === 'INFO') return;

      const eventTime = new Date(event.timestamp);
      const eventAccount = event.accountNumber || '';
      const eventDeviceId = event.deviceId || '';

      const relatedEvents = sortedEvents.filter(e => {
        if (e._id === event._id || processedIds.has(e._id)) return false;
        if ((e.severity || 'UNKNOWN') !== 'INFO') return false;
        const timeDiff = Math.abs(new Date(e.timestamp) - eventTime) / 1000;
        const sameDevice = (e.deviceId || '') === eventDeviceId && eventDeviceId !== '';
        const sameAccount = (e.accountNumber || '') === eventAccount && eventAccount !== '';
        return (sameDevice || sameAccount) && timeDiff <= (eventSeverity === 'HIGH' ? 60 : 30);
      });

      processedIds.add(event._id);
      relatedEvents.forEach(e => processedIds.add(e._id));
      relatedEvents.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));

      clusters.push({ id: `cluster-${event._id}`, primaryEvent: event, relatedEvents, allEvents: [event, ...relatedEvents] });
    });

    // Second pass: remaining INFO as standalone
    sortedEvents.forEach(event => {
      if (processedIds.has(event._id)) return;

      const matchesFilter =
        filterStatus === 'ALL' ||
        (filterStatus === 'NEEDS_REVIEW' && event.alarmStatus === 'PENDING') ||
        (filterStatus === 'CONFIRMED' && event.alarmStatus === 'CONFIRMED') ||
        (filterStatus === 'FALSE' && event.alarmStatus === 'FALSE') ||
        (filterStatus === 'DEFAULT' && event.alarmStatus === 'DEFAULT');

      if (!matchesFilter) return;
      processedIds.add(event._id);
      clusters.push({ id: `cluster-${event._id}`, primaryEvent: event, relatedEvents: [], allEvents: [event] });
    });

    clusters.sort((a, b) => {
      const aTime = new Date(a.primaryEvent.timestamp);
      const bTime = new Date(b.primaryEvent.timestamp);
      if (aTime.getTime() === bTime.getTime()) {
        const rank = s => ({ HIGH: 0, MEDIUM: 1, WARNING: 2, INFO: 3 }[s] ?? 4);
        return rank(a.primaryEvent.severity) - rank(b.primaryEvent.severity);
      }
      return bTime - aTime;
    });

    setClusteredEvents(clusters);
  }, [events, filterStatus]);

  const toggleExpanded = (clusterId) => {
    setExpandedClusters(prev => {
      const next = new Set(prev);
      next.has(clusterId) ? next.delete(clusterId) : next.add(clusterId);
      return next;
    });
  };

  // ── Style helpers ─────────────────────────────────────────────────────────
  const getRowStyle = (event) => {
    switch (event.alarmStatus) {
      case 'CONFIRMED': return 'bg-red-50 hover:bg-red-100';
      case 'FALSE':     return 'bg-green-50 hover:bg-green-100';
      case 'PENDING':   return 'bg-amber-50 hover:bg-amber-100';
      default:          return 'bg-white hover:bg-gray-50';
    }
  };

  const getStatusStyle = (event) => {
    switch (event.alarmStatus) {
      case 'CONFIRMED': return 'bg-red-100 text-red-700 border border-red-200';
      case 'FALSE':     return 'bg-green-100 text-green-700 border border-green-200';
      case 'PENDING':   return 'bg-amber-100 text-amber-700 border border-amber-200';
      default:          return 'bg-gray-100 text-gray-600 border border-gray-200';
    }
  };

  const getStatusText = (event) => {
    switch (event.alarmStatus) {
      case 'CONFIRMED': return 'Confirmed Alarm';
      case 'FALSE':     return 'False Alarm';
      case 'PENDING':   return 'Pending Review';
      default:          return 'Default';
    }
  };

  const getCardBorderStyle = (event) => {
    switch (event.alarmStatus) {
      case 'CONFIRMED': return 'border border-gray-200 border-l-4 border-l-red-400 bg-red-50';
      case 'FALSE':     return 'border border-gray-200 border-l-4 border-l-green-400 bg-green-50';
      case 'PENDING':   return 'border border-gray-200 border-l-4 border-l-amber-400 bg-amber-50';
      default:          return 'border border-gray-200 bg-white';
    }
  };

  const needsAcknowledgment = (event) => event.severity === 'HIGH' && event.alarmStatus === 'PENDING';

  const isVideoAvailableEvent = (event) => event.message && event.message.includes('VIDEOAVAILABLE');

  const needsDownloadButton = (event) => {
    if (isVideoAvailableEvent(event) && event.videoDetails?.status === 'READY') return false;
    return false;
  };

  const isDownloadComplete = (event) =>
    isVideoAvailableEvent(event) && event.videoDetails?.status === 'COMPLETE';

  // ── Shared: action buttons ────────────────────────────────────────────────
  const renderActions = (cluster) => (
    <div className="flex flex-col gap-1.5">
      {needsAcknowledgment(cluster.primaryEvent) && (
        <button
          onClick={(e) => onAcknowledge(cluster.primaryEvent, e)}
          disabled={isLoading}
          className={`w-full ${isLoading ? 'bg-gray-300 cursor-not-allowed' : 'bg-blue-500 hover:bg-blue-600'} text-white px-3 py-1.5 text-xs font-medium rounded-md transition-colors duration-150`}
        >
          {isLoading ? 'Processing…' : 'Acknowledge'}
        </button>
      )}

      {(cluster.primaryEvent.videoDetails?.previewVideo?.data === 'VIDEO-CLIP GENERATED' ||
        cluster.allEvents.some(e => e.videoDetails?.previewVideo?.data === 'VIDEO-CLIP GENERATED') ||
        cluster.primaryEvent.videoDetails?.fullVideo?.data ||
        cluster.allEvents.some(e => e.videoDetails?.fullVideo?.data) ||
        cluster.allEvents.some(e => e.videoDetails?.status === 'RECEIVING')) && (
        <div className="flex gap-1.5 w-full">
          {cluster.primaryEvent.videoDetails?.previewVideo?.data === 'VIDEO-CLIP GENERATED' ||
           cluster.allEvents.some(e => e.videoDetails?.previewVideo?.data === 'VIDEO-CLIP GENERATED') ? (
            <button
              onClick={() => onViewVideo(
                cluster.primaryEvent.videoDetails?.previewVideo?.data === 'VIDEO-CLIP GENERATED'
                  ? cluster.primaryEvent
                  : cluster.allEvents.find(e => e.videoDetails?.previewVideo?.data === 'VIDEO-CLIP GENERATED'),
                'preview'
              )}
              className="flex-1 flex items-center justify-center gap-1 bg-gray-100 hover:bg-gray-200 text-gray-700 py-1.5 text-xs font-medium rounded-md transition-colors duration-150"
            >
              <Video className="w-3.5 h-3.5" /> Clip
            </button>
          ) : cluster.allEvents.some(e => e.videoDetails?.status === 'RECEIVING') ? (
            <div className="flex-1 flex items-center justify-center gap-1 bg-gray-50 text-gray-400 py-1.5 text-xs rounded-md">
              <Loader2 className="w-3.5 h-3.5 animate-spin" /> Clip
            </div>
          ) : null}

          {cluster.primaryEvent.videoDetails?.fullVideo?.data ||
           cluster.allEvents.some(e => e.videoDetails?.fullVideo?.data) ? (
            <button
              onClick={() => onViewVideo(
                cluster.primaryEvent.videoDetails?.fullVideo?.data
                  ? cluster.primaryEvent
                  : cluster.allEvents.find(e => e.videoDetails?.fullVideo?.data),
                'full'
              )}
              className="flex-1 flex items-center justify-center gap-1 bg-gray-100 hover:bg-gray-200 text-gray-700 py-1.5 text-xs font-medium rounded-md transition-colors duration-150"
            >
              <Film className="w-3.5 h-3.5" /> Full
            </button>
          ) : cluster.allEvents.some(e => e.videoDetails?.status === 'RECEIVING') ? (
            <div className="flex-1 flex items-center justify-center gap-1 bg-gray-50 text-gray-400 py-1.5 text-xs rounded-md">
              <Loader2 className="w-3.5 h-3.5 animate-spin" /> Full
            </div>
          ) : null}
        </div>
      )}

      {needsDownloadButton(cluster.primaryEvent) && (
        <button
          onClick={() => onDownloadVideo(cluster.primaryEvent)}
          disabled={isLoading || (cluster.primaryEvent.locationCode && !isPanelOnline(cluster.primaryEvent.locationCode))}
          className={`${
            isLoading || (cluster.primaryEvent.locationCode && !isPanelOnline(cluster.primaryEvent.locationCode))
              ? 'bg-gray-300 cursor-not-allowed'
              : 'bg-purple-500 hover:bg-purple-600'
          } flex items-center gap-1 text-white px-3 py-1.5 text-xs font-medium rounded-md transition-colors duration-150`}
        >
          {cluster.primaryEvent.locationCode && !isPanelOnline(cluster.primaryEvent.locationCode)
            ? <><WifiOff className="h-3.5 w-3.5" /> Offline</>
            : isLoading ? 'Processing…' : 'Download'}
        </button>
      )}

      {isDownloadComplete(cluster.primaryEvent) && (
        <button disabled className="bg-green-500 text-white px-3 py-1.5 text-xs font-medium rounded-md cursor-default">
          Complete
        </button>
      )}
    </div>
  );

  // ── Shared: event detail content (for card view) ──────────────────────────
  const renderEventDetails = (event) => {
    const severity = event.severity || (
      ['VIDEO_ALARM', 'ALARM', 'DURESS'].includes(event.category) ? 'HIGH' : 'UNKNOWN'
    );
    const category = event.category || 'UNKNOWN';
    return (
      <div className="space-y-1">
        <div className="text-sm font-medium text-gray-900">{event.description || 'No description'}</div>
        <div className="flex flex-wrap gap-1.5 mt-1">
          <span className={`text-xs px-2 py-0.5 rounded font-medium ${getSeverityBadgeStyle(severity)}`}>{severity}</span>
          <span className={`text-xs px-2 py-0.5 rounded font-medium ${getCategoryBadgeStyle(category)}`}>{category.replace('_', ' ')}</span>
        </div>
        {event.eventDetails?.yField?.description && (
          <div className="text-xs text-gray-500">Source: {event.eventDetails.yField.description}</div>
        )}
        {event.eventDetails?.zField?.description && (
          <div className="text-xs text-gray-500">Details: {event.eventDetails.zField.description}</div>
        )}
      </div>
    );
  };

  if (!events || events.length === 0) {
    return (
      <div className="flex items-center justify-center py-16 text-gray-400 text-sm">
        No events found
      </div>
    );
  }

  const cellPad = 'px-4 py-3';

  return (
    <div>

      {/* ══════════════════════════════════════════════════════════════════
          DESKTOP TABLE VIEW  (md and above)
      ══════════════════════════════════════════════════════════════════ */}
      <div className="hidden md:block">
        <div className="bg-white shadow-sm rounded-lg overflow-hidden pt-16">

          {/* Fixed column header */}
          <div className="fixed top-28 left-0 right-0 bg-gray-50 border-y border-gray-200 z-40">
            <div className="max-w-full xl:max-w-7xl mx-auto">
              <table className="min-w-full table-fixed">
                <thead>
                  <tr>
                    <th className="w-6 px-4 py-3"></th>
                    <th className="w-32 lg:w-36 px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">Date / Time</th>
                    <th className="w-36 lg:w-40 px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">Device & Account</th>
                    <th style={{ width: '338.47px' }} className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">Event Information</th>
                    <th className="w-40 lg:w-44 px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="w-44 lg:w-48 px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
              </table>
            </div>
          </div>

          {/* Table body */}
          <div className="max-w-full xl:max-w-7xl mx-auto">
            <table className="min-w-full table-fixed divide-y divide-gray-100">
              <tbody className="divide-y divide-gray-100">
                {clusteredEvents.map((cluster) => (
                  <React.Fragment key={cluster.id}>
                    <tr className={`${getRowStyle(cluster.primaryEvent)} transition-colors duration-150`}>
                      <td className={`w-6 ${cellPad} cursor-pointer`} onClick={() => toggleExpanded(cluster.id)}>
                        <div className="flex items-center justify-center">
                          {cluster.relatedEvents.length > 0 && (
                            expandedClusters.has(cluster.id)
                              ? <ChevronDown className="h-4 w-4 text-gray-400" />
                              : <ChevronRight className="h-4 w-4 text-gray-400" />
                          )}
                        </div>
                      </td>

                      <td className={`w-32 lg:w-36 ${cellPad}`}>
                        <div className="text-sm font-medium text-gray-900">{formatDate(cluster.primaryEvent.timestamp)}</div>
                        <div className="text-xs text-gray-500">{formatTime(cluster.primaryEvent.timestamp)}</div>
                        {cluster.relatedEvents.length > 0 && (
                          <div className="mt-1 text-xs text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded-full inline-block">
                            +{cluster.relatedEvents.length} related
                          </div>
                        )}
                      </td>

                      <td className={`w-36 lg:w-40 ${cellPad}`}>
                        <div className="text-sm font-medium text-gray-900">{cluster.primaryEvent.deviceId || 'N/A'}</div>
                        {cluster.primaryEvent.accountNumber && (
                          <div className="text-xs text-gray-500 mt-0.5">{cluster.primaryEvent.accountNumber}</div>
                        )}
                        {getSiteName(cluster.primaryEvent.accountNumber) && (
                          <div className="text-xs px-2 py-0.5 rounded-full font-medium bg-blue-50 text-blue-700 border border-blue-100 inline-block mt-1">
                            {getSiteName(cluster.primaryEvent.accountNumber)}
                          </div>
                        )}
                        {cluster.primaryEvent.locationCode && !isPanelOnline(cluster.primaryEvent.locationCode) && (
                          <div className="mt-1 flex items-center gap-1 text-xs text-red-600 bg-red-50 px-2 py-0.5 rounded-full border border-red-200 w-fit">
                            <WifiOff className="h-3 w-3" /> Offline
                          </div>
                        )}
                      </td>

                      <EventDetailsCell event={cluster.primaryEvent} />

                      <td className={`w-40 lg:w-44 ${cellPad}`}>
                        <span className={`px-2.5 py-1 inline-flex text-xs font-semibold rounded-full ${getStatusStyle(cluster.primaryEvent)}`}>
                          {getStatusText(cluster.primaryEvent)}
                        </span>
                        {(cluster.primaryEvent.statusComment || cluster.primaryEvent.acknowledgedBy) && (
                          <div className="mt-1.5 text-xs text-gray-500 italic break-words">
                            {cluster.primaryEvent.statusComment && `"${cluster.primaryEvent.statusComment}"`}
                            {cluster.primaryEvent.acknowledgedBy && (
                              <div className="text-xs text-gray-400 mt-0.5">by {cluster.primaryEvent.acknowledgedBy}</div>
                            )}
                          </div>
                        )}
                      </td>

                      <td className={`w-44 lg:w-48 ${cellPad}`}>
                        {renderActions(cluster)}
                      </td>
                    </tr>

                    {/* Expanded related events */}
                    {expandedClusters.has(cluster.id) && cluster.relatedEvents.length > 0 && (
                      <tr className="bg-gray-50">
                        <td colSpan="6" className="p-0">
                          <div className="pl-8 border-l-4 border-blue-200 ml-4">
                            <table className="min-w-full table-fixed">
                              <tbody className="divide-y divide-gray-100">
                                {cluster.relatedEvents.map((relatedEvent) => (
                                  <tr key={relatedEvent._id} className="hover:bg-gray-100 transition-colors duration-150">
                                    <td className="w-6 px-4 py-2"></td>
                                    <td className="w-32 lg:w-36 px-4 py-2">
                                      <div className="text-xs font-medium text-gray-700">{formatDate(relatedEvent.timestamp)}</div>
                                      <div className="text-xs text-gray-400">{formatTime(relatedEvent.timestamp)}</div>
                                    </td>
                                    <td className="w-36 lg:w-40 px-4 py-2">
                                      <div className="text-xs font-medium text-gray-700">{relatedEvent.deviceId || 'N/A'}</div>
                                      {relatedEvent.accountNumber && (
                                        <div className="text-xs text-gray-400 mt-0.5">{relatedEvent.accountNumber}</div>
                                      )}
                                      {getSiteName(relatedEvent.accountNumber) && (
                                        <div className="text-xs px-2 py-0.5 rounded-full font-medium bg-blue-50 text-blue-700 border border-blue-100 inline-block mt-1">
                                          {getSiteName(relatedEvent.accountNumber)}
                                        </div>
                                      )}
                                      {relatedEvent.locationCode && !isPanelOnline(relatedEvent.locationCode) && (
                                        <div className="mt-1 flex items-center gap-1 text-xs text-red-600 bg-red-50 px-2 py-0.5 rounded-full border border-red-200 w-fit">
                                          <WifiOff className="h-3 w-3" /> Offline
                                        </div>
                                      )}
                                    </td>
                                    <EventDetailsCell event={relatedEvent} />
                                    <td className="w-40 lg:w-44 px-4 py-2"></td>
                                    <td className="w-44 lg:w-48 px-4 py-2">
                                      <div className="flex flex-col gap-1.5">
                                        {needsDownloadButton(relatedEvent) && (
                                          <button
                                            onClick={() => onDownloadVideo(relatedEvent)}
                                            disabled={isLoading || (relatedEvent.locationCode && !isPanelOnline(relatedEvent.locationCode))}
                                            className={`${
                                              isLoading || (relatedEvent.locationCode && !isPanelOnline(relatedEvent.locationCode))
                                                ? 'bg-gray-300 cursor-not-allowed'
                                                : 'bg-purple-500 hover:bg-purple-600'
                                            } flex items-center gap-1 text-white px-3 py-1.5 text-xs font-medium rounded-md transition-colors duration-150`}
                                          >
                                            {relatedEvent.locationCode && !isPanelOnline(relatedEvent.locationCode)
                                              ? <><WifiOff className="h-3.5 w-3.5" /> Offline</>
                                              : isLoading ? 'Processing…' : 'Download'}
                                          </button>
                                        )}
                                        {isDownloadComplete(relatedEvent) && (
                                          <button disabled className="bg-green-500 text-white px-3 py-1.5 text-xs font-medium rounded-md cursor-default">Complete</button>
                                        )}
                                        {relatedEvent.category === 'VIDEO_ALARM' && (
                                          <div className="flex gap-1.5">
                                            {relatedEvent.videoDetails?.previewVideo?.data === 'VIDEO-CLIP GENERATED' ? (
                                              <button onClick={() => onViewVideo(relatedEvent, 'preview')} className="flex items-center gap-1 bg-gray-100 hover:bg-gray-200 text-gray-700 px-2.5 py-1.5 text-xs font-medium rounded-md transition-colors duration-150">
                                                <Video className="w-3.5 h-3.5" /> Clip
                                              </button>
                                            ) : relatedEvent.videoDetails?.status === 'RECEIVING' ? (
                                              <div className="flex items-center gap-1 bg-gray-50 text-gray-400 px-2.5 py-1.5 text-xs rounded-md">
                                                <Loader2 className="w-3.5 h-3.5 animate-spin" /> Clip
                                              </div>
                                            ) : null}
                                            {relatedEvent.videoDetails?.fullVideo?.data ? (
                                              <button onClick={() => onViewVideo(relatedEvent, 'full')} className="flex items-center gap-1 bg-gray-100 hover:bg-gray-200 text-gray-700 px-2.5 py-1.5 text-xs font-medium rounded-md transition-colors duration-150">
                                                <Film className="w-3.5 h-3.5" /> Full
                                              </button>
                                            ) : relatedEvent.videoDetails?.status === 'RECEIVING' ? (
                                              <div className="flex items-center gap-1 bg-gray-50 text-gray-400 px-2.5 py-1.5 text-xs rounded-md">
                                                <Loader2 className="w-3.5 h-3.5 animate-spin" /> Full
                                              </div>
                                            ) : null}
                                          </div>
                                        )}
                                      </div>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* ══════════════════════════════════════════════════════════════════
          MOBILE CARD VIEW  (below md)
      ══════════════════════════════════════════════════════════════════ */}
      <div className="md:hidden space-y-3 pt-2">
        {clusteredEvents.map((cluster) => (
          <div key={cluster.id} className={`rounded-xl shadow-sm overflow-hidden ${getCardBorderStyle(cluster.primaryEvent)}`}>

            {/* Header: date + status */}
            <div className="flex items-start justify-between px-4 py-3 border-b border-black/5">
              <div>
                <div className="text-xs font-semibold text-gray-800">{formatDate(cluster.primaryEvent.timestamp)}</div>
                <div className="text-xs text-gray-500">{formatTime(cluster.primaryEvent.timestamp)}</div>
              </div>
              <span className={`px-2.5 py-1 text-xs font-semibold rounded-full ${getStatusStyle(cluster.primaryEvent)}`}>
                {getStatusText(cluster.primaryEvent)}
              </span>
            </div>

            {/* Body */}
            <div className="px-4 py-3 space-y-3">
              {/* Device + account + site */}
              <div className="flex items-start justify-between gap-2">
                <div>
                  <span className="text-sm font-semibold text-gray-900">{cluster.primaryEvent.deviceId || 'N/A'}</span>
                  {cluster.primaryEvent.accountNumber && (
                    <span className="text-xs text-gray-500 ml-2">{cluster.primaryEvent.accountNumber}</span>
                  )}
                  {cluster.primaryEvent.locationCode && !isPanelOnline(cluster.primaryEvent.locationCode) && (
                    <div className="flex items-center gap-1 text-xs text-red-600 bg-red-50 px-2 py-0.5 rounded-full border border-red-200 w-fit mt-1">
                      <WifiOff className="h-3 w-3" /> Offline
                    </div>
                  )}
                </div>
                {getSiteName(cluster.primaryEvent.accountNumber) && (
                  <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-blue-50 text-blue-700 border border-blue-100 shrink-0">
                    {getSiteName(cluster.primaryEvent.accountNumber)}
                  </span>
                )}
              </div>

              {renderEventDetails(cluster.primaryEvent)}

              {(cluster.primaryEvent.statusComment || cluster.primaryEvent.acknowledgedBy) && (
                <div className="text-xs text-gray-500 italic">
                  {cluster.primaryEvent.statusComment && `"${cluster.primaryEvent.statusComment}"`}
                  {cluster.primaryEvent.acknowledgedBy && (
                    <div className="text-gray-400 mt-0.5">by {cluster.primaryEvent.acknowledgedBy}</div>
                  )}
                </div>
              )}
            </div>

            {/* Actions footer */}
            <div className="px-4 py-3 border-t border-black/5 bg-black/[0.02]">
              {renderActions(cluster)}
            </div>

            {/* Related events toggle */}
            {cluster.relatedEvents.length > 0 && (
              <button
                onClick={() => toggleExpanded(cluster.id)}
                className="w-full flex items-center justify-between px-4 py-2.5 border-t border-black/5 text-xs font-medium text-gray-500 hover:bg-black/[0.03] transition-colors duration-150"
              >
                <span>+{cluster.relatedEvents.length} related event{cluster.relatedEvents.length > 1 ? 's' : ''}</span>
                {expandedClusters.has(cluster.id)
                  ? <ChevronDown className="h-3.5 w-3.5" />
                  : <ChevronRight className="h-3.5 w-3.5" />}
              </button>
            )}

            {/* Expanded related events */}
            {expandedClusters.has(cluster.id) && cluster.relatedEvents.map((related) => (
              <div key={related._id} className="px-4 py-3 border-t border-blue-100 bg-blue-50/50 space-y-2">
                <div className="text-xs text-gray-500">{formatDate(related.timestamp)} · {formatTime(related.timestamp)}</div>
                {renderEventDetails(related)}
                {related.locationCode && !isPanelOnline(related.locationCode) && (
                  <div className="flex items-center gap-1 text-xs text-red-600 bg-red-50 px-2 py-0.5 rounded-full border border-red-200 w-fit">
                    <WifiOff className="h-3 w-3" /> Offline
                  </div>
                )}
              </div>
            ))}
          </div>
        ))}
      </div>

    </div>
  );
};
